import { SwipeableCards } from "@daformat/react-swipeable-cards";
import React, { useMemo } from "react";
import T from "prop-types";
import styled, { css } from "styled-components/macro";
import Icon from "commons/components/Icon";
import RequestAccessForm from "./RequestAccessForm";
import { useMediaQuery } from "commons/util/useMediaQuery";
import { BREAKPOINTS } from "commons/util/breakpoints";
import Flexbox from "commons/components/Flexbox";

const Box = styled.div`
  position: relative;
  max-width: 100%;
`;

const Root = styled(SwipeableCards.Root)`
  --ease-out-cubic: cubic-bezier(0.215, 0.61, 0.355, 1);

  [data-swipeable-cards-cards] {
    display: grid;
    grid-template-columns: 1fr;
    place-content: center;
    position: relative;
    touch-action: none;
    z-index: 1;

    [data-swipeable-cards-card-wrapper] {
      align-content: center;
      align-self: center;
      grid-area: 1 / 1;
      opacity: calc(1 - clamp(0, var(--stack-index0) - var(--visible-stack-length), 1));
      touch-action: none;
      transform-origin: center 0;
      transition: all 0.2s var(--ease-out-cubic);
      transition-property: opacity, scale, padding-top, margin-top;
      width: 100%;
      /* will-change: opacity, scale, padding-top, margin-top, transform; */
    }
  }

  &:is([data-style="stacked-offset"], [data-style="stacked-rotation"]) {
    [data-swipeable-cards-cards] {
      [data-swipeable-cards-card-wrapper] {
        scale: calc(100% - min(var(--stack-index0), var(--visible-stack-length)) * 10%);
      }
    }
  }

  &[data-style="stacked-offset"] {
    [data-swipeable-cards-cards] {
      [data-swipeable-cards-card-wrapper] {
        // Pick whatever vertical step you want between stacked cards.
        --card-top-distance: clamp(16px, 1vw, 32px);
        --p: calc(var(--card-top-distance) * max(var(--visible-stack-length) - var(--stack-index0), 2));
        --m: calc(
          var(--card-top-distance) *
            (var(--visible-stack-length) - max(var(--visible-stack-length) - var(--stack-index0), 0))
        );
        margin-top: calc(var(--m) * -1);
        padding-top: calc(var(--p));
      }
    }
  }
`;

const SwipeRightButton = styled(SwipeableCards.SwipeRightButton)`
  position: absolute;
  bottom: -56px;
  right: 4px;
  z-index: 1;

  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  color: var(--brown-100);
  font-family: "Stack Sans Headline", sans-serif;
  font-weight: 400;
  width: fit-content;
  padding: 12px 12px 12px 16px;
  border-radius: var(--border-radius-4);
  background: rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: var(--shadow-1), 0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5),
    inset 0 -1px 0 rgba(255, 255, 255, 0.1);

  &:hover {
    background: rgba(255, 255, 255, 0.5);
    box-shadow: var(--shadow-2), 0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5),
      inset 0 -1px 0 rgba(255, 255, 255, 0.1);
  }
`;

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: clamp(320px, 90vw, 560px);
  height: clamp(320px, 90vw, 560px);
  border-radius: var(--border-radius-5);
  padding: 64px 64px 48px 64px;
  cursor: grab;
  transition: backdrop-filter 100ms ease-in-out, box-shadow 250ms ease-in-out;
  overflow: hidden;

  background: rgba(255, 255, 255, 0.2); // to-do: lower opacity of the card or the image unless it is active
  backdrop-filter: blur(40px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5),
    inset 0 -1px 0 rgba(255, 255, 255, 0.1), inset 0 0 20px 10px rgba(255, 255, 255, 1);
  overflow: hidden;

  :before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent);
  }

  :after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 1px;
    height: 100%;
    background: linear-gradient(180deg, transparent, rgba(255, 255, 255, 0.8), transparent, rgba(255, 255, 255, 0.3));
  }

  &:first-child {
    box-shadow: var(--shadow-2);

    &:hover {
      box-shadow: var(--shadow-3);
      backdrop-filter: blur(36px);
    }
  }

  @media (max-width: ${BREAKPOINTS.medium}) {
    padding: 32px;
    height: clamp(320px, 65vh, 640px);
  }
`;

const CardImage = styled.div`
  width: 100%;
  height: 100%;
  min-height: 200px;
  border-radius: var(--border-radius-1);

  ${({ image }) =>
    image &&
    css`
      background-image: url(${image});
    `}

  background-size: contain;
  background-position: left;
  background-repeat: no-repeat;
`;

const CardTitle = styled.h3`
  font-size: 18px;
  line-height: 24px;
  font-weight: 500;
  font-family: "Stack Sans Headline", sans-serif;
  color: var(--brown-100);

  span {
    color: var(--brown-160);
    margin-right: 4px;
  }
`;

const CardDescription = styled.div`
  font-size: 16px;
  line-height: 24px;
  font-weight: 300;

  ul {
    list-style-type: disc;
    padding-left: 16px;
  }

  @media (max-width: ${BREAKPOINTS.medium}) {
    font-size: 14px;
  }
`;

const PagePill = styled.div`
  background-color: var(--neutral-190);
  width: 8px;
  height: 8px;
  border-radius: 4px;
  opacity: 0.4;

  ${({ isActive }) =>
    isActive &&
    css`
      width: 32px;
    `}
`;

const SeeFeaturesTeaser = styled.h2`
  font-size: 18px;
  line-height: 24px;
  font-weight: 400;
  font-family: "Stack Sans Headline", sans-serif;
  z-index: var(--z-index-above);
  color: var(--brown-140);
  margin: auto auto;
`;

const EmptyView = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  text-align: center;
  font-size: 18px;
  font-family: "Stack Sans Headline", sans-serif;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: max-content;

  @media (max-width: ${BREAKPOINTS.medium}) {
    max-width: 90vw;
    text-wrap: balance;
  }
`;

const CardStack = ({ className, cards: initialCards }) => {
  const isScreenMaxMedium = useMediaQuery(BREAKPOINTS.medium);
  const numberOfCards = initialCards.length;

  const cards = useMemo(
    () => [
      ...initialCards.reverse().map((card, cardIndex) => ({
        id: card.id,
        card: (
          <Card>
            <CardTitle>{card.label}</CardTitle>
            <CardDescription>{card.description}</CardDescription>
            <CardImage image={card.image} />
            <Flexbox margin="16px auto 0" gap={6}>
              {[...Array(numberOfCards)].map((_, pillIndex) => (
                <PagePill key={pillIndex} isActive={cardIndex === numberOfCards - pillIndex - 1} />
              ))}
            </Flexbox>
          </Card>
        ),
      })),
      {
        id: "Initial card",
        card: (
          <Card>
            <SeeFeaturesTeaser>Swipe to see features →</SeeFeaturesTeaser>
          </Card>
        ),
      },
    ],
    initialCards
  );

  return (
    <Box>
      <Root
        cards={cards}
        className={className}
        data-style={"stacked-offset"} // "stacked-rotation" or "stacked-offset"
        swipeStyle={"discard"} // "sendToBack" or "discard"
        sendToBackMargin={16}
        emptyView={
          <EmptyView>
            Get early access to everything that&apos;s coming next.
            <RequestAccessForm variant="glassy" />
          </EmptyView>
        }
        onSwipe={() => window.umami?.track("Swipe card")}
      >
        <SwipeableCards.Cards
          visibleStackLength={3}
          style={{ aspectRatio: isScreenMaxMedium ? "560 / 640" : "560 / 560" }}
        />
        <SwipeRightButtonIfNotEmpty />
      </Root>
    </Box>
  );
};

CardStack.propTypes = {
  className: T.string,
  cards: T.arrayOf(T.shape({ id: T.string, label: T.oneOfType([T.object, T.string, T.node]), image: T.any })),
  activeCardId: T.string,
  setActiveCardId: T.func,
};

const SwipeRightButtonIfNotEmpty = () => {
  const stack = SwipeableCards.useSwipeableCardsStack();
  const isScreenMaxMedium = useMediaQuery(BREAKPOINTS.medium);

  if (stack.length === 0 || isScreenMaxMedium) return null;

  return (
    <SwipeRightButton>
      Next
      <Icon name="arrow_forward" />
    </SwipeRightButton>
  );
};

// const ActiveCardObserver = ({ setActiveCardId }) => {
//   const stack = SwipeableCards.useSwipeableCardsStack();
//   useEffect(() => {
//     setActiveCardId(stack[stack.length - 1]?.id);
//   }, [stack]);
//   return null;
// };

// ActiveCardObserver.propTypes = {
//   setActiveCardId: T.func,
// };

export default CardStack;
